import type { ImportRulesResponse, ImportRunSummary, ImportSourceCategory } from '@enp/domain/api';
import type { ProductRole } from '@enp/domain/catalogue';
import { formatCount, formatPercent } from '@enp/domain/format';
import {
  type CategoryRoleMapping,
  type ImportRules,
  matchCategoryMapping,
} from '@enp/domain/migration';
import {
  Badge,
  Button,
  Checkbox,
  cn,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  IconButton,
  Input,
  Label,
  LoadingSkeleton,
  PageHeader,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@enp/ui';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ChevronDown,
  Info,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { ROLE_LABEL, ROLE_ORDER } from '../../../catalogue/labels.js';
import {
  apiErrorText,
  ImportsBreadcrumb,
  Note,
  Section,
  useImportPermissions,
} from '../../../imports/components.js';
import {
  categoriesQuery,
  importKeys,
  invalidateRun,
  replanRun,
  rulesQuery,
  runsQuery,
  saveRules,
} from '../../../imports/queries.js';
import { ApiError } from '../../../lib/api.js';
import { RoomForStickyBar, useDocumentTitle } from '../../../shell/page.js';
import { ErrorPanel } from '../../../shell/route-states.js';

export const Route = createFileRoute('/_app/imports/rules')({
  component: RulesPage,
});

type Draft = Omit<ImportRules, 'decisions'>;
type RolesOrReview = Draft['categoryRoles']['unmapped'];

const withoutDecisions = (rules: ImportRules): Draft => {
  const { decisions: _decisions, ...rest } = rules;
  return rest;
};

const rolesText = (roles: readonly ProductRole[]) =>
  ROLE_ORDER.filter((r) => roles.includes(r))
    .map((r) => ROLE_LABEL[r])
    .join(', ');

/** Spaces made visible ("Imported -␣"): they count when names are matched (UI1A-210). */
const showSpaces = (s: string) => s.replace(/ /g, '␣');

/** The one-line key for "␣", under anything on screen with a space in it. */
function SpaceLegend() {
  return (
    <p className="text-xs text-muted">
      {/* Large enough to read as the mark it is (B-030). */}
      <span
        aria-hidden
        className="inline-block rounded-sm border border-border px-1 font-mono text-sm leading-4 text-fg"
      >
        ␣
      </span>{' '}
      marks a space. Spaces count when names are matched.
    </p>
  );
}

const th =
  'h-9 whitespace-nowrap px-3 text-left text-xs font-semibold uppercase tracking-wide text-muted';
const td = 'border-b border-border px-3 py-2 align-middle';

// ---------------------------------------------------------------- pieces

function RolesMenu({
  roles,
  onChange,
  label,
  disabled,
}: {
  roles: readonly ProductRole[];
  onChange: (roles: ProductRole[]) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" size="sm" disabled={disabled}>
          <span className="sr-only">{label}: </span>
          {roles.length > 0 ? rolesText(roles) : 'Choose roles'}
          <ChevronDown aria-hidden className="text-muted" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>Roles</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {ROLE_ORDER.map((role) => (
          <DropdownMenuCheckboxItem
            key={role}
            checked={roles.includes(role)}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={(on) =>
              onChange(
                on
                  ? ROLE_ORDER.filter((r) => r === role || roles.includes(r))
                  : roles.filter((r) => r !== role),
              )
            }
          >
            {ROLE_LABEL[role]}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function RolesOrReviewSelect({
  value,
  onChange,
  label,
  disabled,
}: {
  value: RolesOrReview;
  onChange: (v: RolesOrReview) => void;
  label: string;
  disabled?: boolean;
}) {
  const current = value === 'review' ? 'review' : value.join('+');
  const single = ROLE_ORDER.map((r) => r as string);
  return (
    <Select
      value={current}
      disabled={disabled}
      onValueChange={(v) => onChange(v === 'review' ? 'review' : (v.split('+') as ProductRole[]))}
    >
      <SelectTrigger aria-label={label} className="w-auto min-w-56">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="review">Ask in the review queue</SelectItem>
        {ROLE_ORDER.map((r) => (
          <SelectItem key={r} value={r}>
            {ROLE_LABEL[r]}
          </SelectItem>
        ))}
        {current !== 'review' && !single.includes(current) && (
          <SelectItem value={current}>{rolesText(value as ProductRole[])}</SelectItem>
        )}
      </SelectContent>
    </Select>
  );
}

/** A list of short texts (suffixes), each in its own box so spaces are kept exactly. */
function SuffixList({
  values,
  onChange,
  label,
  disabled,
}: {
  values: string[];
  onChange: (values: string[]) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-start gap-2">
      {values.map((v, i) => (
        <span key={i} className="flex items-start gap-1">
          <span className="flex flex-col">
            <Input
              value={v}
              aria-label={`${label} ${i + 1}`}
              className="h-control-sm w-36 font-mono text-sm"
              disabled={disabled}
              aria-invalid={v === '' ? true : undefined}
              onChange={(e) => onChange(values.map((x, j) => (j === i ? e.target.value : x)))}
            />
            {/* Spaces matter ("Cake REC" vs "Cake-REC"): shown, not guessed (UI1A-10). */}
            {v.includes(' ') && (
              <span className="mt-0.5 font-mono text-xs text-muted" aria-hidden>
                {v.replace(/ /g, '␣')}
              </span>
            )}
          </span>
          <IconButton
            variant="ghost"
            label={`Remove ${label.toLowerCase()} ${i + 1}`}
            icon={<Trash2 />}
            disabled={disabled}
            onClick={() => onChange(values.filter((_, j) => j !== i))}
          />
        </span>
      ))}
      <Button
        variant="ghost"
        size="sm"
        icon={<Plus aria-hidden />}
        disabled={disabled}
        onClick={() => onChange([...values, ''])}
      >
        Add
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------- category roles

function CategoryTable({
  categories,
  uncategorised,
  draft,
  decisions,
  setMappings,
  disabled,
}: {
  categories: ImportSourceCategory[];
  uncategorised: number;
  draft: Draft;
  decisions: ImportRules['decisions'];
  setMappings: (m: CategoryRoleMapping[]) => void;
  disabled: boolean;
}) {
  const { mappings, unmapped } = draft.categoryRoles;
  const unmappedCount = categories.filter(
    (c) =>
      matchCategoryMapping(mappings, c.name) < 0 &&
      !decisions.some((d) => d.sourceRef === c.source_ref && d.decision.kind === 'set_roles'),
  ).length;

  const setExact = (name: string, roles: ProductRole[]) => {
    const at = mappings.findIndex(
      (m) => m.match === 'exact' && m.value.trim().toLowerCase() === name.trim().toLowerCase(),
    );
    if (roles.length === 0) {
      if (at >= 0) setMappings(mappings.filter((_, i) => i !== at));
      return;
    }
    if (at >= 0) setMappings(mappings.map((m, i) => (i === at ? { ...m, roles } : m)));
    // An exact rule goes first: it wins over any "starts with" rule.
    else setMappings([{ match: 'exact', value: name, roles }, ...mappings]);
  };

  return (
    <>
      <p className="mb-3 flex flex-wrap items-center gap-2 text-sm text-muted">
        {formatCount(categories.length)} categories in the latest import
        {uncategorised > 0 && <> · {formatCount(uncategorised)} items have no category</>}
        {unmappedCount > 0 ? (
          <Badge tone="warning">
            <TriangleAlert aria-hidden />
            {formatCount(unmappedCount)} not mapped
          </Badge>
        ) : (
          <Badge tone="success">Every category is mapped</Badge>
        )}
      </p>
      <div className="-mx-5 -mb-2 overflow-x-auto">
        <table aria-label="Source categories" className="w-full border-collapse text-body">
          <thead>
            <tr className="border-y border-border bg-surface-sunken">
              <th scope="col" className={cn(th, 'pl-5')}>
                Category
              </th>
              <th scope="col" className={cn(th, 'text-right')}>
                Items
              </th>
              <th scope="col" className={th}>
                Roles
              </th>
              <th scope="col" className={cn(th, 'pr-5')}>
                Decided by
              </th>
            </tr>
          </thead>
          <tbody className="[&>tr:last-child>td]:border-b-0">
            {categories.map((c) => {
              const decided = decisions.find(
                (d) => d.sourceRef === c.source_ref && d.decision.kind === 'set_roles',
              );
              const at = matchCategoryMapping(mappings, c.name);
              const mapping = mappings[at];
              const exact = mapping?.match === 'exact';
              const roles =
                decided?.decision.kind === 'set_roles'
                  ? decided.decision.roles
                  : (mapping?.roles ?? (unmapped === 'review' ? [] : unmapped));
              const notMapped = !decided && !mapping;
              let by: ReactNode;
              if (decided) by = 'A decision in the review queue';
              else if (mapping)
                by = exact
                  ? 'Its own rule'
                  : `Rule: name starts with “${showSpaces(mapping.value)}”`;
              else if (unmapped === 'review') by = 'Not mapped: asked in the review queue';
              else by = 'The default for unmapped categories';
              return (
                <tr key={c.source_ref} className={cn(notMapped && 'bg-state-warning-subtle')}>
                  <th scope="row" className={cn(td, 'pl-5 text-left font-medium')}>
                    <span className="flex flex-wrap items-center gap-2">
                      {notMapped && (
                        <TriangleAlert aria-hidden className="size-4 text-state-warning" />
                      )}
                      {c.name}
                      {c.deleted && <Badge tone="outline">Deleted in the source</Badge>}
                    </span>
                  </th>
                  <td className={cn(td, 'text-right tabular-nums')}>{formatCount(c.items)}</td>
                  <td className={td}>
                    {/* A decision in the review queue wins over any rule (B-023): a
                        change here would not apply, so it can't be made here. */}
                    <RolesMenu
                      roles={roles}
                      label={`Roles for ${c.name}`}
                      disabled={disabled || decided !== undefined}
                      onChange={(next) => setExact(c.name, next)}
                    />
                  </td>
                  <td className={cn(td, 'pr-5 text-sm', notMapped ? 'text-fg' : 'text-muted')}>
                    {by}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {mappings.some((m) => m.match === 'prefix' && m.value.includes(' ')) && (
        <div className="mt-4">
          <SpaceLegend />
        </div>
      )}
    </>
  );
}

function MappingRules({
  draft,
  setMappings,
  disabled,
}: {
  draft: Draft;
  setMappings: (m: CategoryRoleMapping[]) => void;
  disabled: boolean;
}) {
  const { mappings } = draft.categoryRoles;
  const update = (i: number, patch: Partial<CategoryRoleMapping>) =>
    setMappings(mappings.map((m, j) => (j === i ? { ...m, ...patch } : m)));
  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold text-fg">
        Rules on the category name, first match wins
      </h3>
      {mappings.length === 0 ? (
        <p className="text-sm text-muted">
          No rules yet. Set roles on a category above, or add a rule.
        </p>
      ) : (
        <ol
          className="grid grid-cols-[1.5rem_auto_minmax(8rem,14rem)_auto_auto] items-center justify-start gap-2"
          aria-label="Category rules"
        >
          {mappings.map((m, i) => (
            <li key={i} className="col-span-5 grid grid-cols-subgrid items-center">
              <span className="text-right text-sm tabular-nums text-muted">{i + 1}.</span>
              <Select
                value={m.match}
                disabled={disabled}
                onValueChange={(v) => update(i, { match: v as CategoryRoleMapping['match'] })}
              >
                <SelectTrigger aria-label={`Rule ${i + 1}: how to match`} className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="exact">Is exactly</SelectItem>
                  <SelectItem value="prefix">Starts with</SelectItem>
                </SelectContent>
              </Select>
              <Input
                value={m.value}
                aria-label={`Rule ${i + 1}: category name`}
                className="w-full"
                disabled={disabled}
                aria-invalid={m.value.trim() === '' ? true : undefined}
                onChange={(e) => update(i, { value: e.target.value })}
              />
              <RolesMenu
                roles={m.roles}
                label={`Rule ${i + 1}: roles`}
                disabled={disabled}
                onChange={(roles) => update(i, { roles })}
              />
              <IconButton
                variant="ghost"
                label={`Remove rule ${i + 1}`}
                icon={<Trash2 />}
                disabled={disabled}
                onClick={() => setMappings(mappings.filter((_, j) => j !== i))}
              />
            </li>
          ))}
        </ol>
      )}
      <div>
        <Button
          variant="secondary"
          size="sm"
          icon={<Plus aria-hidden />}
          disabled={disabled}
          onClick={() =>
            setMappings([...mappings, { match: 'prefix', value: '', roles: ['sellable'] }])
          }
        >
          Add a rule
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- expiry states + twins

function ExpiryStates({
  draft,
  update,
  disabled,
}: {
  draft: Draft;
  update: (g: Draft['stateGroups']) => void;
  disabled: boolean;
}) {
  const g = draft.stateGroups;
  const nameId = useId();
  const sameCatId = useId();
  if (g.states.length === 0) {
    return (
      <Note icon={Info}>
        <p>Finding expiry states by SKU is off: the states aren’t set up for this account yet.</p>
      </Note>
    );
  }
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <Label htmlFor={nameId}>Name of the state rule</Label>
        <Input
          id={nameId}
          value={g.ruleName}
          className="max-w-md"
          disabled={disabled}
          onChange={(e) => update({ ...g, ruleName: e.target.value })}
        />
      </div>
      <div className="-mx-5 overflow-x-auto">
        <table aria-label="Expiry states" className="w-full border-collapse text-body">
          <thead>
            <tr className="border-y border-border bg-surface-sunken">
              <th scope="col" className={cn(th, 'pl-5')}>
                State
              </th>
              <th scope="col" className={th}>
                SKU ends with
              </th>
              <th scope="col" className={th}>
                Name ends with
              </th>
              <th scope="col" className={cn(th, 'pr-5')}>
                Sold at
              </th>
            </tr>
          </thead>
          <tbody>
            {g.states.map((s, i) => (
              <tr key={s.key}>
                <th scope="row" className={cn(td, 'pl-5 text-left font-medium capitalize')}>
                  {s.key}
                </th>
                <td className={td}>
                  <Input
                    value={s.skuSuffix}
                    aria-label={`${s.key}: SKU ends with`}
                    className="h-control-sm w-24 font-mono text-sm"
                    disabled={disabled}
                    aria-invalid={s.skuSuffix.trim() === '' ? true : undefined}
                    onChange={(e) =>
                      update({
                        ...g,
                        states: g.states.map((x, j) =>
                          j === i ? { ...x, skuSuffix: e.target.value } : x,
                        ),
                      })
                    }
                  />
                </td>
                <td className={td}>
                  <SuffixList
                    values={s.nameSuffixes}
                    label={`${s.key}: name ending`}
                    disabled={disabled}
                    onChange={(nameSuffixes) =>
                      update({
                        ...g,
                        states: g.states.map((x, j) => (j === i ? { ...x, nameSuffixes } : x)),
                      })
                    }
                  />
                </td>
                <td className={cn(td, 'pr-5 text-sm text-muted')}>
                  {s.sellable
                    ? s.markdownBp > 0
                      ? `${formatPercent(s.markdownBp)} off`
                      : 'Full price'
                    : 'Not for sale'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {g.states.some((st) => st.nameSuffixes.some((n) => n.includes(' '))) && <SpaceLegend />}
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-sm font-medium text-fg">
          Which state’s barcode becomes the product’s barcode
        </legend>
        <RadioGroup
          value={g.gtinState ?? 'none'}
          onValueChange={(v) => update({ ...g, gtinState: v === 'none' ? null : v })}
          className="flex flex-wrap gap-4"
          disabled={disabled}
        >
          {[...g.states.map((s) => s.key), 'none'].map((k) => (
            <label key={k} className="flex items-center gap-2 text-body capitalize">
              <RadioGroupItem value={k} />
              {k === 'none' ? 'None' : k}
            </label>
          ))}
        </RadioGroup>
        <p className="text-sm text-muted">
          The other states’ barcodes are recorded as not imported (one product, one barcode).
        </p>
      </fieldset>
      <div className="flex items-center gap-2">
        <Checkbox
          id={sameCatId}
          checked={g.requireSameCategory}
          disabled={disabled}
          onCheckedChange={(v) => update({ ...g, requireSameCategory: v === true })}
        />
        <Label htmlFor={sameCatId} className="font-normal">
          The states of one product must share a category
        </Label>
      </div>
    </div>
  );
}

/** Problems that would make the server refuse the rules, in words. */
function validate(draft: Draft): string[] {
  const problems: string[] = [];
  draft.categoryRoles.mappings.forEach((m, i) => {
    if (m.value.trim() === '') problems.push(`Rule ${i + 1} needs a category name.`);
    if (m.roles.length === 0) problems.push(`Rule ${i + 1} needs at least one role.`);
  });
  const suffixes = draft.stateGroups.states.map((s) => s.skuSuffix.trim().toUpperCase());
  if (suffixes.some((s) => s === '')) problems.push('Every expiry state needs a SKU ending.');
  if (new Set(suffixes).size !== suffixes.length)
    problems.push('The expiry states’ SKU endings must differ.');
  if (
    draft.stateGroups.states.some(
      (s) => s.nameSuffixes.length === 0 || s.nameSuffixes.some((n) => n.trim() === ''),
    )
  ) {
    problems.push('Every expiry state needs at least one name ending, and none may be empty.');
  }
  if (draft.stateGroups.ruleName.trim() === '') problems.push('The state rule needs a name.');
  if ([...draft.twins.skuSuffixes, ...draft.twins.nameSuffixes].some((s) => s.trim() === '')) {
    problems.push('Recipe endings can’t be empty.');
  }
  return problems;
}

// ---------------------------------------------------------------- page

function RulesEditor({
  server,
  latestRun,
  catalogueRun,
  onSaved,
  recheckNote,
}: {
  server: ImportRulesResponse;
  /** The newest run (re-checked after a save when it awaits review or a load). */
  latestRun: ImportRunSummary | undefined;
  /** The newest run that read a catalogue (its categories are listed). */
  catalogueRun: ImportRunSummary | undefined;
  /** The rules are saved; re-check `replannable` (a step of its own, CR1-22). */
  onSaved: (saved: ImportRulesResponse, replannable: ImportRunSummary | undefined) => void;
  /** How the re-check after the last save went, when it needs saying. */
  recheckNote: ReactNode;
}) {
  const queryClient = useQueryClient();
  const { canEdit } = useImportPermissions();
  const [draft, setDraft] = useState<Draft>(() => withoutDecisions(server.rules));
  const [problems, setProblems] = useState<string[]>([]);
  const [conflict, setConflict] = useState(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify(withoutDecisions(server.rules));
  const withCatalogue = catalogueRun;
  const categories = useQuery({
    ...categoriesQuery(withCatalogue?.id ?? ''),
    enabled: withCatalogue !== undefined,
  });
  const replannable =
    latestRun && (latestRun.status === 'awaiting_review' || latestRun.status === 'ready')
      ? latestRun
      : undefined;
  const disabled = !canEdit;

  // Saving the rules and re-checking the import are two steps with their own outcomes: a
  // failed re-check never reads as "not saved" (CR1-22).
  const save = useMutation({
    mutationFn: () => saveRules({ base_revision: server.revision, rules: draft }),
    onSuccess: (saved) => {
      // The re-check that follows says how it went on its own (UI1A-203).
      toast.success('Import rules saved');
      onSaved(saved, replannable);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409 && e.code === 'rules_changed')
        setConflict(true);
      else setProblems([e instanceof ApiError ? e.message : 'The rules weren’t saved.']);
    },
  });

  const setMappings = (mappings: CategoryRoleMapping[]) =>
    setDraft((d) => ({ ...d, categoryRoles: { ...d.categoryRoles, mappings } }));

  const reload = () => void queryClient.invalidateQueries({ queryKey: importKeys.rules() });

  const submit = () => {
    const found = validate(draft);
    setProblems(found);
    if (found.length === 0) save.mutate();
  };

  return (
    <>
      <PageHeader
        eyebrow={<ImportsBreadcrumb page="Import rules" />}
        title="Import rules"
        description="How records from your source become products: which categories mean what, how expiry-state SKUs are spelt and how recipe items are named. Decisions from the review queue are kept separately."
        actions={
          canEdit ? (
            <>
              <Button
                variant="secondary"
                icon={<RotateCcw aria-hidden />}
                disabled={!dirty || save.isPending}
                onClick={() => {
                  setDraft(withoutDecisions(server.rules));
                  setProblems([]);
                }}
              >
                Undo changes
              </Button>
              {conflict ? (
                <Button icon={<RefreshCw aria-hidden />} onClick={reload}>
                  Reload the rules
                </Button>
              ) : (
                <Button
                  icon={<Save aria-hidden />}
                  loading={save.isPending}
                  disabled={!dirty}
                  onClick={submit}
                >
                  {save.isPending ? 'Saving…' : replannable ? 'Save and re-check' : 'Save rules'}
                </Button>
              )}
            </>
          ) : undefined
        }
      />
      <div className={cn('flex flex-col gap-6', canEdit && dirty && 'pb-24')}>
        {recheckNote}
        {!canEdit && (
          <Note icon={Info}>
            <p>You can read the rules; changing them needs catalogue editing on all branches.</p>
          </Note>
        )}
        {conflict && (
          <Note icon={TriangleAlert} tone="warning" role="alert">
            <p className="font-medium">Someone else changed the rules while you were editing.</p>
            <p className="text-muted">
              Reload the rules to see their version, then make your change again.
            </p>
          </Note>
        )}
        {problems.length > 0 && (
          <Note icon={TriangleAlert} tone="danger" role="alert">
            <p className="font-medium">The rules weren’t saved:</p>
            <ul className="ml-4 list-disc">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </Note>
        )}
        {dirty && replannable && (
          <Note icon={Info} tone="info">
            <p>
              Saving checks the current import again with the new rules.{' '}
              <Link
                to="/imports/runs/$runId"
                params={{ runId: replannable.id }}
                className="font-medium text-primary-text underline underline-offset-2"
              >
                Open that import
              </Link>
            </p>
          </Note>
        )}

        <Section
          id="categories"
          title="Category roles"
          description="Which source categories hold products for sale, ingredients, packaging or supplies. The category itself stays a reporting group."
        >
          <div className="flex flex-col gap-6">
            {!withCatalogue ? (
              <EmptyState
                compact
                icon={Info}
                title="No source categories yet"
                description="Run an import to list every category of your source here, with the ones not mapped yet highlighted."
              />
            ) : categories.isPending ? (
              <LoadingSkeleton variant="table" label="Loading categories" count={6} />
            ) : categories.isError ? (
              <ErrorPanel
                error={categories.error}
                titleAs="h2"
                what="The categories"
                onRetry={() => void categories.refetch()}
              />
            ) : (
              <CategoryTable
                categories={categories.data.items}
                uncategorised={categories.data.uncategorised_items}
                draft={draft}
                decisions={server.rules.decisions}
                setMappings={setMappings}
                disabled={disabled}
              />
            )}
            <MappingRules draft={draft} setMappings={setMappings} disabled={disabled} />
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium text-fg">Categories no rule matches</p>
                <RolesOrReviewSelect
                  label="Categories no rule matches"
                  value={draft.categoryRoles.unmapped}
                  disabled={disabled}
                  onChange={(unmapped) =>
                    setDraft((d) => ({ ...d, categoryRoles: { ...d.categoryRoles, unmapped } }))
                  }
                />
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium text-fg">Items with no category</p>
                <RolesOrReviewSelect
                  label="Items with no category"
                  value={draft.categoryRoles.uncategorised}
                  disabled={disabled}
                  onChange={(uncategorised) =>
                    setDraft((d) => ({
                      ...d,
                      categoryRoles: { ...d.categoryRoles, uncategorised },
                    }))
                  }
                />
              </div>
            </div>
          </div>
        </Section>

        <Section
          id="expiry"
          title="Expiry states by SKU"
          description="Products sold as fresh, markdown, clearance and expired under separate SKUs become one product with states. Set how the source spells them."
        >
          <ExpiryStates
            draft={draft}
            disabled={disabled}
            update={(stateGroups) => setDraft((d) => ({ ...d, stateGroups }))}
          />
        </Section>

        <Section
          id="twins"
          title="Recipe items"
          description="Items that only hold another product’s recipe (for example “Carrot cake REC”). Their ingredients become that product’s draft recipe."
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium text-fg">SKU ends with</p>
              <SuffixList
                values={draft.twins.skuSuffixes}
                label="Recipe SKU ending"
                disabled={disabled}
                onChange={(skuSuffixes) =>
                  setDraft((d) => ({ ...d, twins: { ...d.twins, skuSuffixes } }))
                }
              />
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium text-fg">Name ends with</p>
              <SuffixList
                values={draft.twins.nameSuffixes}
                label="Recipe name ending"
                disabled={disabled}
                onChange={(nameSuffixes) =>
                  setDraft((d) => ({ ...d, twins: { ...d.twins, nameSuffixes } }))
                }
              />
            </div>
            {[...draft.twins.skuSuffixes, ...draft.twins.nameSuffixes].some((v) =>
              v.includes(' '),
            ) && <SpaceLegend />}
          </div>
        </Section>
        <p className="text-sm text-muted">
          Revision {formatCount(server.revision)} · {formatCount(server.rules.decisions.length)}{' '}
          {server.rules.decisions.length === 1 ? 'decision' : 'decisions'} from the review queue
          kept with them.
        </p>
      </div>
      {/* Save where the edit happened, not only at the top of a long page (UI1A-10). */}
      {canEdit && dirty && (
        <div
          role="region"
          aria-label="Unsaved rule changes"
          data-sticky-bar
          className="sticky bottom-4 z-10 ml-auto mt-6 flex w-fit flex-wrap items-center gap-3 rounded-lg border border-border bg-surface-raised px-4 py-3 shadow-lg"
        >
          {/* A field focused under the bar scrolls clear of it (B-030). */}
          <RoomForStickyBar />
          <p className="text-body text-fg">
            {conflict ? 'Someone else changed the rules: reload them first.' : 'Unsaved changes'}
          </p>
          <Button
            variant="ghost"
            disabled={save.isPending}
            onClick={() => {
              setDraft(withoutDecisions(server.rules));
              setProblems([]);
            }}
          >
            Undo changes
          </Button>
          {/* After a conflict the way on is their version (UI1A-210). */}
          {conflict ? (
            <Button icon={<RefreshCw aria-hidden />} onClick={reload}>
              Reload the rules
            </Button>
          ) : (
            <Button loading={save.isPending} onClick={submit}>
              {save.isPending ? 'Saving…' : replannable ? 'Save and re-check' : 'Save rules'}
            </Button>
          )}
        </div>
      )}
    </>
  );
}

/** The re-check that follows a save, and what to say when it didn't happen (CR1-22). */
function useRecheckAfterSave() {
  const queryClient = useQueryClient();
  const [failed, setFailed] = useState<{
    runId: string;
    title: string;
    message: string;
    /** The import is past re-checking (loaded, cancelled or superseded). */
    final: boolean;
  }>();
  const recheck = useMutation({
    mutationFn: (runId: string) => replanRun(runId),
    onMutate: () => setFailed(undefined),
    onSuccess: async (_run, runId) => {
      toast.success('The current import is being checked again', {
        description: 'Its report updates by itself in a moment.',
      });
      await invalidateRun(queryClient, runId);
    },
    onError: async (e, runId) => {
      const text = apiErrorText(e, 'the source', 'The import wasn’t checked again');
      const final = text.code === 'run_not_reviewable' || text.code === 'run_not_ready';
      setFailed({ runId, title: text.title, message: text.message, final });
      await invalidateRun(queryClient, runId);
    },
  });
  const note = failed ? (
    <Note
      icon={failed.final ? Info : TriangleAlert}
      tone={failed.final ? 'info' : 'warning'}
      role={failed.final ? 'status' : 'alert'}
    >
      <p className="font-medium">Rules saved; the import wasn’t re-checked.</p>
      <p className="text-muted">
        {failed.final
          ? 'This import can’t be re-checked any more (it’s loaded or cancelled). Your rules apply to the next import.'
          : `${failed.title}. ${failed.message} The rules are safe; check the import again to use them.`}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {!failed.final && (
          <Button
            variant="secondary"
            size="sm"
            icon={<RefreshCw aria-hidden />}
            loading={recheck.isPending}
            onClick={() => recheck.mutate(failed.runId)}
          >
            {recheck.isPending ? 'Checking again…' : 'Check it again'}
          </Button>
        )}
        <Link
          to="/imports/runs/$runId"
          params={{ runId: failed.runId }}
          className="text-sm font-medium text-primary-text underline underline-offset-2"
        >
          Open that import
        </Link>
      </div>
    </Note>
  ) : null;
  return { recheck: (runId: string) => recheck.mutate(runId), note };
}

function RulesPage() {
  useDocumentTitle('Import rules');
  const queryClient = useQueryClient();
  const rules = useQuery(rulesQuery());
  const runs = useInfiniteQuery(runsQuery());
  // Lives here, not in the editor: a save gives the editor fresh rules (and a fresh draft).
  const after = useRecheckAfterSave();
  if (rules.isPending || runs.isPending) {
    return (
      <>
        <PageHeader eyebrow={<ImportsBreadcrumb page="Import rules" />} title="Import rules" />
        <LoadingSkeleton variant="form" label="Loading the import rules" />
      </>
    );
  }
  const failed = rules.error ?? runs.error;
  if (failed) {
    return (
      <div className="max-w-2xl">
        <ErrorPanel
          error={failed}
          what="The import rules"
          onRetry={() => {
            void rules.refetch();
            void runs.refetch();
          }}
        />
      </div>
    );
  }
  if (!rules.data || !runs.data) return null;
  const all = runs.data.pages.flatMap((p) => p.items);
  return (
    <RulesEditor
      // Fresh rules from the server (after a save or a reload) start a fresh draft.
      key={`${rules.data.revision}:${rules.data.updated_at ?? ''}`}
      server={rules.data}
      latestRun={all[0]}
      catalogueRun={all.find((r) => r.report_status !== null)}
      recheckNote={after.note}
      onSaved={(saved, replannable) => {
        queryClient.setQueryData(importKeys.rules(), saved);
        if (replannable) after.recheck(replannable.id);
      }}
    />
  );
}
